// LiveInClubPage — the V1 Manager-facing Live In Club surface.
// Composes the xstate page-state machine (filter tab + search value),
// the Tanstack Query cache (Talent + status data, fetched via the
// `listLiveInClubServerFn` server function), and the design-system
// primitives (Tabs, EmptyState, CountBadge, TextInput w/ icon slot,
// Typography).
//
// Page surface (top → bottom):
//   - Header chrome: Club name (left) + today's date + N-live count
//     badge. Renders inside the page body rather than the layout's
//     header_content slot to avoid the layout↔route header-content
//     plumbing decision (option for a follow-up).
//   - "Quick filters" label
//   - Filter tabs (All / In Club / Not In, with counts)
//   - Search input (TextInput with search-icon start slot)
//   - Sectioned list (eyebrow-style section headers with the count
//     pushed to the far-right)

import { useQuery } from '@tanstack/react-query';
import { useMachine } from '@xstate/react';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { CountBadge } from '~/_shared/design-system/ui/atoms/badge/count/count-badge';
import { Icon } from '~/_shared/design-system/ui/atoms/icon/icon';
import { TextInput } from '~/_shared/design-system/ui/atoms/text-input/text-input';
import { Typography } from '~/_shared/design-system/ui/atoms/typography/typography';
import { useHeaderSlot } from '~/_shared/design-system/ui/layouts/authenticated-layout/authenticated-layout';
import { EmptyState } from '~/_shared/design-system/ui/molecules/empty-state/empty-state';
import { Tabs } from '~/_shared/design-system/ui/molecules/tabs/tabs';
import {
	getClubSettingsQueryKey,
	getClubSettingsServerFn,
} from '~/bounded-contexts/club/get-club-settings.fn';
import { listLiveInClubServerFn } from '../../application/list-live-in-club.fn';
import type { LiveInClubEntry } from '../../domain/types';
import { LiveBadge } from '../atoms/badges/live-badge/live-badge';
import { AutoClockoutBanner } from '../molecules/auto-clockout-banner/auto-clockout-banner';
import { useNow } from '../molecules/auto-clockout-banner/use-now';

import './live-in-club-page.css';
import { LiveInClubSkeleton } from '../molecules/live-in-club-skeleton/live-in-club-skeleton';
import {
	type FilterTab,
	liveInClubPageMachine,
} from './live-in-club-page-machine';
import {
	liveInClubQueryKey as _queryKey,
	resolveRowTimestamp as _resolveRowTimestamp,
} from './query-key';
import { TalentRowItem } from './talent-row-item';
import { TalentRowStoreProvider } from './talent-row-store';

// Re-exports preserve the existing public surface (route loader,
// test imports) after the helpers moved to `./query-key`.
export const liveInClubQueryKey = _queryKey;
export const resolveRowTimestamp = _resolveRowTimestamp;

interface SectionedView {
	in_club: readonly LiveInClubEntry[];
	not_in_club: readonly LiveInClubEntry[];
	total: number;
}

/**
 * Sort by stage name (locale-aware, case-insensitive) so the list reads
 * predictably even when the cache changes order on cross-section moves.
 * Returns a NEW array — the input is treated as read-only.
 */
function sortByStageName(
	entries: readonly LiveInClubEntry[],
): readonly LiveInClubEntry[] {
	return [...entries].sort((a, b) =>
		a.stageName.localeCompare(b.stageName, undefined, { sensitivity: 'base' }),
	);
}

/**
 * Apply the search filter (case-insensitive substring match on
 * stage_name) to a sectioned shape. Returns the post-filter sections
 * plus a total count across both. Both sections are sorted by stage name
 * (default ordering); the search filter preserves that order.
 */
function applySearch(
	in_club: readonly LiveInClubEntry[],
	not_in_club: readonly LiveInClubEntry[],
	search: string,
): SectionedView {
	const sorted_in = sortByStageName(in_club);
	const sorted_not = sortByStageName(not_in_club);
	const normalized = search.trim().toLowerCase();
	if (!normalized) {
		return {
			in_club: sorted_in,
			not_in_club: sorted_not,
			total: sorted_in.length + sorted_not.length,
		};
	}
	const match = (e: LiveInClubEntry) =>
		e.stageName.toLowerCase().includes(normalized);
	const matched_in = sorted_in.filter(match);
	const matched_not = sorted_not.filter(match);
	return {
		in_club: matched_in,
		not_in_club: matched_not,
		total: matched_in.length + matched_not.length,
	};
}

// Native Intl date formatter for the page chrome. Per-row timestamp
// formatters now live in `./query-key.ts` so the row component can
// reuse them without re-importing this module.
const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
	weekday: 'short',
	month: 'short',
	day: 'numeric',
});

interface SectionProps {
	title: string;
	entries: readonly LiveInClubEntry[];
	club_id: string;
	/**
	 * False during SSR + the initial client render; true after the
	 * post-hydration effect fires. Gates locale/zone-sensitive text
	 * (the per-row timestamp) so SSR emits no zone-dependent text and
	 * hydration stays consistent.
	 */
	is_mounted: boolean;
}

const Section = ({ title, entries, club_id, is_mounted }: SectionProps) => (
	<section className='live-in-club-page__section'>
		<header className='live-in-club-page__section-header'>
			<Typography
				variant='eyebrow'
				as='h2'
				className='live-in-club-page__section-title'
			>
				{title}
			</Typography>
			<CountBadge
				value={entries.length}
				aria-label={`${entries.length} ${title}`}
			/>
		</header>
		<ul className='live-in-club-page__list'>
			{entries.map((entry) => (
				<TalentRowItem
					key={entry.id}
					entry={entry}
					is_mounted={is_mounted}
					club_id={club_id}
				/>
			))}
		</ul>
	</section>
);

interface LiveInClubPageProps {
	id?: string;
	/**
	 * The current tenant's Club id. Threaded into the Tanstack Query
	 * cache key so different Clubs do not share a single cache entry
	 * (was the root cause of the post-sign-out stale-data bug). The
	 * server function still resolves its own scope from the request, so
	 * this prop influences cache partitioning only — never what gets
	 * fetched.
	 */
	club_id: string;
	/**
	 * The Club name rendered in the page header chrome. Sourced from the
	 * `_authenticated` route context in production (see the route file
	 * `src/routes/_authenticated/live-in-club.tsx`); passed in here as a
	 * prop so the component stays pure and unit-testable without a
	 * Tanstack Router provider.
	 */
	club_name?: string;
	/**
	 * The `auto-clockout` feature flag (#10). Read in the route via
	 * `useFeatureFlag` (inside the router) and passed in here so this component
	 * stays router-free and unit-testable. Defaults dark.
	 */
	auto_clockout_enabled?: boolean;
}

const LiveInClubPage = (props: LiveInClubPageProps) => (
	// TalentRowStoreProvider hoists each row's xstate actor + toast handle
	// above the section boundary, so the section swap on a toggle doesn't
	// reset the row's state. See talent-row-store.tsx for the contract.
	<TalentRowStoreProvider>
		<LiveInClubPageBody {...props} />
	</TalentRowStoreProvider>
);

const LiveInClubPageBody = ({
	id = 'LiveInClubPage',
	club_id,
	club_name = '',
	auto_clockout_enabled = false,
}: LiveInClubPageProps) => {
	const [snapshot, send] = useMachine(liveInClubPageMachine);
	const search_input_id = useId();
	// Falsy until the layout's header-slot callback ref attaches; an
	// `HTMLElement` once it does. See the chrome render below for the
	// portal-or-nothing contract (no inline fallback — that produced a
	// page shift on hydration).
	const header_slot = useHeaderSlot();

	// SSR runs in UTC on the worker; the client runs in the viewer's
	// local zone. Locale/zone-formatted text would tear hydration if
	// rendered during SSR, so defer all such text (the chrome date and
	// the per-row "Live since …" / "Last live on …" lines) to a
	// post-mount effect. SSR emits empty placeholders; the client fills
	// them in on hydration.
	const [is_mounted, set_is_mounted] = useState(false);
	useEffect(() => set_is_mounted(true), []);
	const formatted_date = is_mounted ? DATE_FORMATTER.format(new Date()) : '';

	const query = useQuery({
		queryKey: liveInClubQueryKey(club_id),
		queryFn: () => listLiveInClubServerFn(),
	});

	// Auto-clockout banner inputs (#10, ADR 0010). Evaluated unconditionally at
	// the top (hook rules) even though the banner only renders on the data path
	// below. The `auto_clockout_enabled` flag is read in the route (inside the
	// router) and passed in as a prop so this component stays router-free and
	// unit-testable; the settings drive the countdown; `useNow` ticks it each
	// minute (SSR-safe).
	const now = useNow();
	const club_settings_query = useQuery({
		queryKey: getClubSettingsQueryKey(club_id),
		queryFn: () => getClubSettingsServerFn(),
	});

	const { selected_tab, search_value } = snapshot.context;

	// The route loader now WARMS the roster cache without blocking first paint
	// (#85), so the query can be pending on first render. Branch order:
	//   - error → the refresh/refetch failed;
	//   - pending (no data yet) → the roster skeleton (also the SSR/first-paint
	//     state, since the loader no longer awaits);
	//   - resolved null → no active session (server fn returned null); the parent
	//     guard redirects on the next navigation, render nothing meanwhile.
	if (query.isError) {
		return (
			<main id={id} className='live-in-club-page'>
				<Typography id={`${id}-error`} variant='error'>
					Couldn't load the Live In Club list. Try refreshing the page.
				</Typography>
			</main>
		);
	}
	if (query.isPending) {
		return <LiveInClubSkeleton id={`${id}-skeleton`} />;
	}
	if (!query.data) {
		// No active session — the parent route guard will redirect on the
		// next navigation. Render nothing in the meantime.
		return null;
	}

	const sections = applySearch(
		query.data.in_club,
		query.data.not_in_club,
		search_value,
	);

	const all_count = query.data.in_club.length + query.data.not_in_club.length;
	const in_count = query.data.in_club.length;
	const not_count = query.data.not_in_club.length;

	const show_in_club = selected_tab === 'all' || selected_tab === 'in_club';
	const show_not_in_club =
		selected_tab === 'all' || selected_tab === 'not_in_club';

	// Visible count combines tab + search. Search alone might leave a
	// section populated, but if the active tab hides it the visible
	// total can still be 0 and we should show the no-matches state.
	const visible_total =
		(show_in_club ? sections.in_club.length : 0) +
		(show_not_in_club ? sections.not_in_club.length : 0);

	// No Talent at the Club at all — different empty state from "no
	// search matches". The roster onboarding surface is V2 (#13); for
	// now this is informational.
	if (all_count === 0) {
		return (
			<main id={id} className='live-in-club-page'>
				<EmptyState
					id={`${id}-empty-roster`}
					icon={<Icon name='inbox' size='lg' />}
					title='No Talent at this Club yet'
					description='Add Talent from the Manage Talent screen when it ships.'
				/>
			</main>
		);
	}

	const chrome = (
		<header className='live-in-club-page__chrome'>
			<div className='live-in-club-page__chrome-text'>
				{club_name ? (
					<Typography
						id={`${id}-club-name`}
						variant='eyebrow'
						as='p'
						className='live-in-club-page__chrome-club'
					>
						{club_name}
					</Typography>
				) : null}
				<Typography
					id={`${id}-date`}
					variant='subheading'
					as='h1'
					className='live-in-club-page__chrome-date'
				>
					{formatted_date}
				</Typography>
			</div>
			<LiveBadge
				id={`${id}-live-count`}
				value={in_count}
				aria-label={`${in_count} live in the club`}
			/>
		</header>
	);

	return (
		<main id={id} className='live-in-club-page'>
			{/* Portal the chrome into the layout's header slot. Render
			 *  nothing when no slot is available (SSR / first paint
			 *  before the layout's callback ref attaches, or isolated
			 *  tests / Storybook stories rendered outside the layout) —
			 *  the inline fallback we tried initially caused a visible
			 *  page shift on hydration as the chrome moved from inline
			 *  into the portal. */}
			{header_slot ? createPortal(chrome, header_slot) : null}

			{/* Auto-clockout warning (#10). Self-hides unless the flag is on, the
			 *  Club has a configured close time, and ≥1 Talent is clocked in. */}
			<AutoClockoutBanner
				id={`${id}-auto-clockout`}
				enabled={auto_clockout_enabled}
				isMounted={is_mounted}
				settings={club_settings_query.data}
				inClubCount={in_count}
				now={now}
			/>

			{/* `suppressHydrationWarning` here is targeted at password-manager
			 *  browser extensions (ProtonPass, 1Password, etc.) that mutate
			 *  this subtree to attach a form-detection hook (e.g.
			 *  `data-protonpass-form=""`) BEFORE React hydrates. Without
			 *  the suppression React refuses to patch up the subtree and
			 *  subsequent renders can de-sync from the DOM. The flag only
			 *  silences attribute/text mismatches on this single element,
			 *  not its descendants — the children's hydration is still
			 *  validated normally. */}
			<div className='live-in-club-page__filters' suppressHydrationWarning>
				<Typography
					id={`${id}-filters-label`}
					variant='eyebrow'
					as='p'
					className='live-in-club-page__filters-label'
				>
					Quick filters
				</Typography>
				<Tabs
					value={selected_tab}
					on_value_change={(value) =>
						send({ type: 'SELECT_TAB', value: value as FilterTab })
					}
				>
					<Tabs.List aria-label='Filter Talent by presence'>
						<Tabs.Tab value='all' count={all_count}>
							All
						</Tabs.Tab>
						<Tabs.Tab value='in_club' count={in_count}>
							In Club
						</Tabs.Tab>
						<Tabs.Tab value='not_in_club' count={not_count}>
							Not In
						</Tabs.Tab>
					</Tabs.List>
					{/* Panels are required by the Tabs API contract (see
					 *  src/_shared/design-system/ui/molecules/tabs/tabs.tsx).
					 *  Empty here — the list body below renders outside the
					 *  panels so it stays mounted across tab switches. */}
					<Tabs.Panel value='all'>{null}</Tabs.Panel>
					<Tabs.Panel value='in_club'>{null}</Tabs.Panel>
					<Tabs.Panel value='not_in_club'>{null}</Tabs.Panel>
				</Tabs>
				<div className='live-in-club-page__search'>
					<label htmlFor={search_input_id} className='sr-only'>
						Search by stage name
					</label>
					<TextInput
						id={search_input_id}
						type='search'
						value={search_value}
						placeholder='Search by name…'
						start_icon={<Icon name='search' size='sm' />}
						onChange={(e) =>
							send({ type: 'UPDATE_SEARCH', value: e.target.value })
						}
					/>
				</div>
			</div>

			{visible_total === 0 ? (
				<EmptyState
					id={`${id}-empty-search`}
					icon={<Icon name='search' size='lg' />}
					title={`No Talent matches "${search_value}"`}
					description='Try a different search.'
				/>
			) : (
				<div
					className={classlist(
						'live-in-club-page__sections',
						selected_tab !== 'all' && 'live-in-club-page__sections--single',
					)}
				>
					{show_in_club ? (
						<Section
							title='In Club'
							entries={sections.in_club}
							club_id={club_id}
							is_mounted={is_mounted}
						/>
					) : null}
					{show_not_in_club ? (
						<Section
							title='Not In'
							entries={sections.not_in_club}
							club_id={club_id}
							is_mounted={is_mounted}
						/>
					) : null}
				</div>
			)}
		</main>
	);
};

export type { LiveInClubPageProps };
export { LiveInClubPage };
